import type {
  AIResponse,
  VehicleInfo,
  AIDiagnosisCause,
  AIFixSteps,
  ChatSession,
} from "../types";

// Use VPSReporting server proxy for RAG requests
const VPS_SERVER_BASE_URL =
  process.env.REACT_APP_API_URI || "http://localhost:5001";

interface RagChatResponse {
  choices: Array<{
    message: {
      role: string;
      content: string;
    };
    finish_reason?: string;
  }>;
}

interface RagChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface RagChatRequest {
  messages: RagChatMessage[];
}

const parseJsonResponse = <T>(text: string): T => {
  try {
    // Clean up the response text
    let cleanedText = text.trim();

    // Remove markdown code blocks if present
    cleanedText = cleanedText.replace(/^```json\s*|```\s*$/g, "").trim();

    // Try to extract JSON from the response
    const jsonMatch = cleanedText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      [cleanedText] = jsonMatch;
    }

    // Parse JSON
    const parsed = JSON.parse(cleanedText);
    return parsed;
  } catch (error) {
    console.error("Failed to parse JSON response:", text, error);
    throw new Error(`Invalid JSON response: ${error}`);
  }
};

// Make API call via VPSReporting server RAG proxy
const makeRagRequest = async (messages: RagChatMessage[]): Promise<string> => {
  const payload: RagChatRequest = {
    messages,
  };

  try {
    // Making request to VPS server RAG proxy
    const response = await fetch(`${VPS_SERVER_BASE_URL}/find-a-fix/rag/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Include authorization header if user is logged in
        Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error("VPS server RAG proxy error:", response.status, errorBody);
      throw new Error(
        `Server RAG proxy error: ${response.status} ${response.statusText} - ${errorBody}`,
      );
    }

    const data: RagChatResponse = await response.json();
    const content = data.choices[0]?.message?.content || "";

    if (!content) {
      console.warn("Empty response from RAG via proxy:", data);
      throw new Error("Empty response from RAG service");
    }

    return content;
  } catch (error) {
    console.error("Error calling RAG via server proxy:", error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to communicate with RAG service: ${error}`);
  }
};

// Chat session class for maintaining conversation state
export class RagChatSession {
  private messages: RagChatMessage[] = [];

  constructor(systemInstruction: string, initialUserMessage: string) {
    // RAG agent already has instructions built-in, just send the user message
    this.messages.push({
      role: "user",
      content: initialUserMessage,
    });
  }

  async sendMessage(message: { message: string }): Promise<{ text: string }> {
    // Add user message
    this.messages.push({
      role: "user",
      content: message.message,
    });

    try {
      // Get response from RAG
      const response = await makeRagRequest(this.messages);

      // Add assistant response to history
      this.messages.push({
        role: "assistant",
        content: response,
      });

      return { text: response };
    } catch (error) {
      console.error("Error in RAG chat session:", error);
      throw error;
    }
  }

  getHistory(): RagChatMessage[] {
    return [...this.messages];
  }
}

// Image analysis for vehicle fault detection
export const analyzePartImagesForFault = async (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _fileContent: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _mimeType: string,
): Promise<string> => {
  // RAG doesn't support image analysis yet, return placeholder
  console.warn("RAG service does not support image analysis");
  return "Image analysis not supported with RAG service. Please describe the issue verbally.";
};

// Start a new chat session
export const startChatSession = async (
  vehicleInfo: VehicleInfo,
): Promise<{ chat: ChatSession; response: AIResponse }> => {
  try {
    // Create initial prompt with vehicle information
    const initialPrompt = `Vehicle Information:
- Make: ${vehicleInfo.make}
- Model: ${vehicleInfo.model}
- Year: ${vehicleInfo.year}
- Problem Description: ${vehicleInfo.faultDescription}
- DTC Codes: ${vehicleInfo.dtcCodes || "None provided"}

Start the diagnostic conversation by asking the first multiple choice question to help identify the root cause of the issue.`;

    const chat = new RagChatSession("", initialPrompt);
    const response = await chat.sendMessage({ message: "" });

    const parsedResponse = parseJsonResponse<AIResponse>(response.text);
    return { chat, response: parsedResponse };
  } catch (error) {
    console.error("Error starting RAG chat session:", error);
    throw new Error("Failed to start diagnostic conversation with RAG");
  }
};

// Continue an existing chat session
export const continueChatSession = async (
  chat: ChatSession,
  message: string,
): Promise<AIResponse> => {
  try {
    const response = await chat.sendMessage({ message });
    return parseJsonResponse<AIResponse>(response.text);
  } catch (error) {
    console.error("Error continuing RAG chat session:", error);
    throw new Error("Failed to continue conversation with RAG");
  }
};

// Get detailed fix steps for a specific cause
export const getFixSteps = async (
  vehicleInfo: VehicleInfo,
  cause: AIDiagnosisCause,
): Promise<AIFixSteps> => {
  try {
    const prompt = `Vehicle: ${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}
Problem: ${vehicleInfo.faultDescription}
DTC Codes: ${vehicleInfo.dtcCodes || "None"}

Identified Cause: ${cause.cause}
Likelihood: ${cause.likelihood}
Reason: ${cause.reason}

Provide detailed step-by-step repair instructions for this specific issue. Return ONLY JSON in this format:
{
  "type": "fix",
  "steps": ["string"]
}

Make the steps specific, actionable, and include safety warnings where appropriate.`;

    const messages: RagChatMessage[] = [
      {
        role: "user",
        content: prompt,
      },
    ];

    const response = await makeRagRequest(messages);
    return parseJsonResponse<AIFixSteps>(response);
  } catch (error) {
    console.error("Error getting fix steps from RAG:", error);
    throw new Error("Failed to get repair instructions from RAG");
  }
};

// Get detailed information about a specific cause
export const getCauseDetails = async (
  vehicleInfo: VehicleInfo,
  cause: AIDiagnosisCause,
): Promise<string> => {
  try {
    const prompt = `Explain in detail why "${cause.cause}" is ${cause.likelihood.toLowerCase()} for a ${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model} with the problem: "${vehicleInfo.faultDescription}"

Provide technical details about this diagnosis.`;

    const messages: RagChatMessage[] = [
      {
        role: "user",
        content: prompt,
      },
    ];

    const response = await makeRagRequest(messages);
    return response;
  } catch (error) {
    console.error("Error getting cause details from RAG:", error);
    return "Unable to provide detailed explanation at this time.";
  }
};

// Look up DTC code information
export const lookupDtcCode = async (code: string): Promise<string> => {
  try {
    const prompt = `Provide detailed information about diagnostic trouble code (DTC): ${code}

Include:
- What the code means
- Common causes
- Symptoms
- Basic diagnostic steps

Be concise but comprehensive.`;

    const messages: RagChatMessage[] = [
      {
        role: "user",
        content: prompt,
      },
    ];

    const response = await makeRagRequest(messages);
    return response;
  } catch (error) {
    console.error("Error looking up DTC code from RAG:", error);
    return `Unable to lookup DTC code ${code} at this time.`;
  }
};
